import { useEffect, useState, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { selectAccountData } from "../redux/main/accountSlice";
import LoadingSection from "../components/LoadingSection";
import DynamicTitle from "../components/DynamicTitle";
import { getNodeApiUrl } from "../config/api.config";
import RightModal from "../components/templates/RightModal";
import AddEditSystem from "../components/systemsPage/AddEditSystem";
import ViewSystem from "../components/systemsPage/ViewSystem";
import { closeModal, selectRightModalId, selectRightModalIsOpen, selectRightModalmode, setModal } from "../redux/rightModalSlice";

export default function CustomersMap() {
    const dispatch = useDispatch();
    const accountData = useSelector(selectAccountData);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [systems, setSystems] = useState([]);
    const [currentZoom, setCurrentZoom] = useState(12);
    const mapRef = useRef(null);
    const markersRef = useRef([]);
    const openInfoWindowRef = useRef(null); // Per gestire l'InfoWindow aperta
    
    // RightModal gestita tramite Redux
    const rightModalIsOpen = useSelector(selectRightModalIsOpen);
    const rightModalMode = useSelector(selectRightModalmode);
    const rightModalSystemId = useSelector(selectRightModalId);
    
    // Funzione chiamata quando l'impianto viene salvato
    const handleSystemSaveComplete = () => {
        // Chiudi il modal
        dispatch(closeModal());
        // Ricarica gli impianti
        loadSystems();
    };
    
    // Riferimento alle coordinate dell'headquarter (default a Roma)
    const headquarterCoordinates = accountData?.settings?.headquarterCoordinates || { lat: 41.9028, lng: 12.4964 };
    
    // Carica gli impianti dal server
    const loadSystems = async () => {
        try {
            if (!accountData?._id) return;
            
            // Prova entrambe le chiavi del token
            let token = localStorage.getItem('token');
            if (!token) {
                token = localStorage.getItem('installamelo-auth-token');
            }
            
            
            const response = await fetch(
                `${getNodeApiUrl('/systems/systemsList')}?accountId=${accountData._id}`,
                {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                        'auth-token': token || ''
                    }
                }
            );
            
            
            if (!response.ok) {
                const errorText = await response.text();
                console.error('Errore response:', errorText);
                
                // Se il token è scaduto o non valido, NON rimuovere il token né reindirizzare
                // dato che altre pagine funzionano correttamente
                if (response.status === 401) {
                    console.warn('Errore 401 specifico per questa API, ma token valido per altre pagine');
                    setError('Errore di autorizzazione per questa funzionalità. Token valido per altre pagine.');
                    return;
                }
                
                throw new Error(`Errore HTTP: ${response.status} - ${errorText}`);
            }
            
            const data = await response.json();
            
            if (data.success && data.data) {
                // Filtra solo gli impianti che hanno coordinate geoData
                const systemsWithGeo = data.data.filter(system => 
                    system.geoData && 
                    system.geoData.coordinates && 
                    system.geoData.coordinates.length === 2
                );
                setSystems(systemsWithGeo);
            } else {
                // Prova a usare direttamente i dati se non c'è la struttura success/data
                if (Array.isArray(data)) {
                    const systemsWithGeo = data.filter(system => 
                        system.geoData && 
                        system.geoData.coordinates && 
                        system.geoData.coordinates.length === 2
                    );
                    setSystems(systemsWithGeo);
                }
            }
        } catch (err) {
            console.error('Errore nel caricamento degli impianti:', err);
            setError('Errore nel caricamento degli impianti: ' + err.message);
        }
    };
    
    // Inizializza la mappa Google Maps
    const initializeMap = () => {
        // Verifica se Google Maps è disponibile
        if (typeof window.google === 'undefined' || !window.google.maps) {
            console.error('Google Maps non è caricato');
            setError('Libreria Google Maps non disponibile');
            setIsLoading(false);
            return;
        }
        
        // Coordinate dell'headquarter
        let lat = 41.9028; // Default a Roma
        let lng = 12.4964;
        
        
        if (headquarterCoordinates) {
            // Gestisci sia formato array [lat, lng] che oggetto {lat, lng}
            if (Array.isArray(headquarterCoordinates) && headquarterCoordinates.length === 2) {
                // Formato array: [lat, lng]
                lat = parseFloat(headquarterCoordinates[0]) || 41.9028;
                lng = parseFloat(headquarterCoordinates[1]) || 12.4964;
            } else if (typeof headquarterCoordinates === 'object') {
                // Formato oggetto: {lat, lng}
                if (typeof headquarterCoordinates.lat === 'number') {
                    lat = headquarterCoordinates.lat;
                } else if (typeof headquarterCoordinates.lat === 'string') {
                    lat = parseFloat(headquarterCoordinates.lat) || 41.9028;
                }
                
                if (typeof headquarterCoordinates.lng === 'number') {
                    lng = headquarterCoordinates.lng;
                } else if (typeof headquarterCoordinates.lng === 'string') {
                    lng = parseFloat(headquarterCoordinates.lng) || 12.4964;
                }
            }
        }
        
        
        // Crea la mappa Google Maps
        const map = new window.google.maps.Map(document.getElementById('map'), {
            center: { lat, lng },
            zoom: currentZoom,
            mapTypeId: window.google.maps.MapTypeId.ROADMAP
        });
        
        // Aggiungi marker per l'headquarter con icona a goccia rossa
        const headquarterMarker = new window.google.maps.Marker({
            position: { lat, lng },
            map: map,
            title: 'Sede Principale',
            icon: {
                url: 'http://maps.google.com/mapfiles/ms/icons/red-dot.png',
                scaledSize: new window.google.maps.Size(32, 32)
            }
        });
        
        // InfoWindow per l'headquarter
        const headquarterInfoWindow = new window.google.maps.InfoWindow({
            content: '<div><h4 style="margin: 0 0 4px 0; font-weight: bold;">Sede Principale</h4><p style="margin: 0; color: #666;">Coordinate Headquarter</p></div>'
        });
        
        headquarterMarker.addListener('click', () => {
            // Chiudi eventuali InfoWindow aperte
            if (openInfoWindowRef.current) {
                openInfoWindowRef.current.close();
            }
            headquarterInfoWindow.open(map, headquarterMarker);
            openInfoWindowRef.current = headquarterInfoWindow;
        });
        
        // Gestisci il cambio di zoom
        map.addListener('zoom_changed', () => {
            const newZoom = map.getZoom();
            setCurrentZoom(newZoom);
            updateMarkersVisibility(newZoom, map);
        });
        
        mapRef.current = map;
        setIsLoading(false);
    };
    
    // Aggiorna la visibilità dei marker in base allo zoom
    const updateMarkersVisibility = (zoom, map = mapRef.current) => {
        if (!map) return;
        
        // Rimuovi tutti i marker esistenti degli impianti
        markersRef.current.forEach(marker => {
            marker.setMap(null);
        });
        markersRef.current = [];
        
        // Mostra tutti gli impianti disponibili (784)
        const maxSystems = systems.length;
        
        // Aggiungi i marker degli impianti
        const systemsToShow = systems;
        systemsToShow.forEach(system => {
            if (system.geoData && system.geoData.coordinates) {
                const [lng, lat] = system.geoData.coordinates;
                
                // Crea marker per gli impianti con icona a goccia blu
                const systemMarker = new window.google.maps.Marker({
                    position: { lat, lng },
                    map: map,
                    title: `${system.customerLabel} - ${system.title}`,
                    icon: {
                        url: 'http://maps.google.com/mapfiles/ms/icons/blue-dot.png',
                        scaledSize: new window.google.maps.Size(32, 32)
                    }
                });
                
                // Crea il contenuto del popup
                const typesHtml = system.typesList && system.typesList.length > 0 
                    ? system.typesList.map(type => `<li>${type.typeLabel}</li>`).join('')
                    : '<li>Nessun tipo specificato</li>';
                
                const popupContent = `
                    <div style="min-width: 200px;">
                        <h4 style="margin: 0 0 8px 0; font-weight: bold; color: #1f2937;">${system.customerLabel || 'Cliente non specificato'}</h4>
                        <p style="margin: 0 0 4px 0; font-weight: 600; color: #374151;">Impianto: ${system.title || 'N/A'}</p>
                        <p style="margin: 0 0 8px 0; color: #6b7280; font-size: 0.9em;">${system.customerAddressLabel || 'Indirizzo non disponibile'}</p>
                        <div style="margin-top: 8px;">
                            <p style="margin: 0 0 4px 0; font-weight: 600; color: #374151;">Tipi di impianto:</p>
                            <ul style="margin: 0; padding-left: 16px; color: #6b7280; font-size: 0.9em;">
                                ${typesHtml}
                            </ul>
                        </div>
                        <div style="margin-top: 12px; padding-top: 8px; border-top: 1px solid #e5e7eb; display: flex; gap: 8px;">
                            <button onclick="window.openViewSystemModal('${system._id}')" 
                                    style="display: inline-block; background-color: #059669; color: white; padding: 6px 12px; border-radius: 4px; border: none; cursor: pointer; font-size: 0.85em; font-weight: 500;">
                                👁️ Visualizza Impianto
                            </button>
                            <button onclick="window.openEditSystemModal('${system._id}')" 
                                    style="display: inline-block; background-color: #2563eb; color: white; padding: 6px 12px; border-radius: 4px; border: none; cursor: pointer; font-size: 0.85em; font-weight: 500;">
                                ✏️ Modifica Impianto
                            </button>
                        </div>
                    </div>
                `;
                
                // InfoWindow per gli impianti
                const systemInfoWindow = new window.google.maps.InfoWindow({
                    content: popupContent
                });
                
                systemMarker.addListener('click', () => {
                    // Chiudi eventuali InfoWindow aperte
                    if (openInfoWindowRef.current) {
                        openInfoWindowRef.current.close();
                    }
                    systemInfoWindow.open(map, systemMarker);
                    openInfoWindowRef.current = systemInfoWindow;
                });
                
                markersRef.current.push(systemMarker);
            }
        });
    };
    
    // Carica Google Maps API
    useEffect(() => {
        const loadGoogleMaps = () => {
            // Verifica se Google Maps è già caricato
            if (typeof window.google !== 'undefined' && window.google.maps) {
                setTimeout(initializeMap, 100);
                return;
            }
            
            // Carica Google Maps API
            const script = document.createElement('script');
            script.src = 'https://maps.googleapis.com/maps/api/js?key=AIzaSyB_N0O0gIG23fPEFHMJUceTSoWCnmGRJ-4&libraries=places';
            script.async = true;
            script.defer = true;
            script.onload = () => {
                setTimeout(initializeMap, 100);
            };
            script.onerror = () => {
                setError('Errore nel caricamento di Google Maps');
                setIsLoading(false);
            };
            document.head.appendChild(script);
        };
        
        if (accountData) {
            loadGoogleMaps();
            loadSystems();
        }
        
        // Registra le funzioni globali per aprire i modal
        window.openEditSystemModal = (systemId) => {
            // Chiudi eventuali InfoWindow aperte
            if (openInfoWindowRef.current) {
                openInfoWindowRef.current.close();
            }
            // Apri il modal di modifica
            dispatch(setModal({ 
                mode: 'edit', 
                percentuageWidth: 90, 
                id: systemId 
            }));
        };
        
        window.openViewSystemModal = (systemId) => {
            // Chiudi eventuali InfoWindow aperte
            if (openInfoWindowRef.current) {
                openInfoWindowRef.current.close();
            }
            // Apri il modal di visualizzazione
            dispatch(setModal({ 
                mode: 'view', 
                percentuageWidth: 90, 
                id: systemId 
            }));
        };
        
        // Cleanup
        return () => {
            // Rimuovi tutti i marker
            markersRef.current.forEach(marker => {
                if (marker && marker.setMap) {
                    marker.setMap(null);
                }
            });
            markersRef.current = [];
            mapRef.current = null;
            // Rimuovi le funzioni globali
            delete window.openEditSystemModal;
            delete window.openViewSystemModal;
        };
    }, [accountData]);
    
    // Aggiorna i marker quando cambiano gli impianti o lo zoom
    useEffect(() => {
        if (mapRef.current && systems.length > 0) {
            updateMarkersVisibility(currentZoom);
        }
    }, [systems, currentZoom]);

    return (
        <div className="page">
            {isLoading && <LoadingSection isFixed />}
            <DynamicTitle title="Mappa Impianti" />
            
            <h1><span className="font-light">Impianti / </span>Mappa</h1>
            
            {error && (
                <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
                    <p>{error}</p>
                </div>
            )}
            
            <div className="box p-4">
                <div className="mb-4 text-sm text-gray-600">
                    <p>Zoom corrente: <span className="font-semibold">{currentZoom}</span></p>
                    <p>Impianti visualizzati: <span className="font-semibold">{systems.length}</span></p>
                    <p className="text-xs mt-1">
                        🗺️ Tutti gli impianti sono visualizzati sulla mappa. 
                        <span className="text-red-600">Rosso</span> = Sede, 
                        <span className="text-blue-600">Blu</span> = Impianti
                    </p>
                </div>
                
                <div 
                    id="map" 
                    style={{ 
                        height: '700px', 
                        width: '100%', 
                        borderRadius: '8px',
                        border: '1px solid #e5e7eb'
                    }}
                    className="shadow-md"
                ></div>
            </div>
            
            {/* Modal per visualizzazione e modifica impianto */}
            {rightModalIsOpen && (
                <RightModal>
                    {(rightModalMode === 'add' || rightModalMode === 'edit') && (
                        <AddEditSystem 
                            systemId={rightModalSystemId} 
                            onSaveComplete={handleSystemSaveComplete}
                        />
                    )}
                    {rightModalMode === 'view' && rightModalSystemId && (
                        <ViewSystem systemId={rightModalSystemId} />
                    )}
                </RightModal>
            )}
        </div>
    );
}
